
/**
 * Bulletproof date parsing for date-only fields from the backend.
 * 
 * This utility ensures that date-only strings (YYYY-MM-DD) are parsed
 * exactly as calendar dates without any timezone shifts, regardless of
 * the user's browser locale or timezone settings.
 * 
 * The strategy:
 * 1. Parse YYYY-MM-DD strings manually to extract year, month, day
 * 2. Create dates at noon local time to avoid DST boundaries
 * 3. Never use ISO strings or UTC conversions for date-only fields
 * 4. Treat dates as calendar dates, not timestamps
 */

/**
 * Parse a date-only string (YYYY-MM-DD) into a Date object that represents
 * that exact calendar date in the local timezone, avoiding any timezone shifts.
 * 
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object representing the exact calendar date, or null if invalid
 */
export function parseTaskDate(dateString: string | Date | null | undefined): Date | null {
  if (!dateString) return null;
  
  // If already a Date object, create a new date at noon to ensure stability
  if (dateString instanceof Date) {
    const year = dateString.getFullYear();
    const month = dateString.getMonth();
    const day = dateString.getDate();
    return new Date(year, month, day, 12, 0, 0, 0);
  }
  
  // Handle YYYY-MM-DD format
  if (typeof dateString === 'string') {
    // Use regex to validate and extract parts
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
      // Try to parse other date formats
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      
      // Convert to noon local time
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      return new Date(year, month, day, 12, 0, 0, 0);
    }
    
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1; // JavaScript months are 0-indexed
    const day = parseInt(match[3], 10);
    
    // Create date at noon local time to avoid DST issues
    // Using noon ensures we're always in the middle of the day
    const date = new Date(year, month, day, 12, 0, 0, 0);
    
    // Validate the date components match what we put in
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month ||
      date.getDate() !== day
    ) {
      return null;
    }
    
    return date;
  }
  
  return null;
}

/**
 * Create a date at local noon for a given date string.
 * This is the most stable time for date-only operations as it's
 * furthest from DST transitions which happen at 2-3 AM.
 * 
 * @param dateString - Date string in any format
 * @returns Date object at noon local time
 */
export function createLocalNoonDate(dateString: string | Date): Date {
  const parsed = parseTaskDate(dateString);
  if (!parsed) {
    // Fallback: create current date at noon
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);
  }
  return parsed;
}

/**
 * Format a Date object to YYYY-MM-DD string for backend submission.
 * This preserves the exact calendar date without timezone conversion.
 * 
 * @param date - Date object to format
 * @returns YYYY-MM-DD string
 */
export function formatTaskDateForBackend(date: Date | null | undefined): string | null {
  if (!date) return null;
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Format a Date object for display in US format (MM/DD/YYYY).
 * This preserves the exact calendar date without timezone conversion.
 * 
 * @param date - Date object to format
 * @returns MM/DD/YYYY string
 */
export function formatTaskDateForDisplay(date: Date | null | undefined): string {
  if (!date) return '';
  
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${month}/${day}/${year}`;
}

/**
 * Get the calendar date string in YYYY/MM/DD format for FullCalendar.
 * This format is used by FullCalendar for date comparisons.
 * 
 * @param date - Date object to format
 * @returns YYYY/MM/DD string
 */
export function getCalendarDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}/${month}/${day}`;
}

/**
 * Calculate the difference in days between two dates.
 * This uses calendar dates, ignoring time components.
 * 
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Number of days between dates (inclusive)
 */
export function calculateDaysDifference(startDate: Date, endDate: Date): number {
  // Normalize both dates to noon to avoid DST issues
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 12, 0, 0, 0);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 12, 0, 0, 0);
  
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  
  // Add 1 to make it inclusive (e.g., same day = 1 day)
  return diffDays + 1;
}

/**
 * Signed calendar-day shift from one date to another (exclusive: same day = 0, one day later = 1,
 * one day earlier = -1). Same noon normalisation as `calculateDaysDifference`, so DST changes
 * never turn a whole-day move into 22/23/25 hours of drift.
 *
 * Use this to move a task (and its subtasks) by the distance a drag travelled. Use
 * `calculateDaysDifference` for a duration, where inclusive counting is what you want.
 *
 * @param from - Original date
 * @param to - New date
 * @returns Whole calendar days from `from` to `to` (negative when `to` is earlier)
 */
export function calculateDayShift(from: Date, to: Date): number {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 12, 0, 0, 0);
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 12, 0, 0, 0);
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Add days to a date while preserving the calendar date.
 * This handles DST transitions correctly.
 * 
 * @param date - Base date
 * @param days - Number of days to add (can be negative)
 * @returns New date with days added
 */
export function addDaysToDate(date: Date, days: number): Date {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate() + days, 12, 0, 0, 0);
  return result;
}

/**
 * Check if a date string represents today's date in the local timezone.
 * 
 * @param dateString - Date string to check
 * @returns True if the date is today
 */
export function isToday(dateString: string | Date): boolean {
  const date = parseTaskDate(dateString);
  if (!date) return false;
  
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

/**
 * Convert a date to ISO string for FullCalendar events.
 * FullCalendar expects ISO strings but we ensure they represent
 * the correct local date without timezone shifts.
 * 
 * @param date - Date to convert
 * @returns ISO string representing the local date
 */
export function toCalendarISOString(date: Date): string {
  // Get the local date components
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  // Create a new date at noon local time to avoid DST issues
  const localDate = new Date(year, month, day, 12, 0, 0, 0);
  
  // Format as YYYY-MM-DD string instead of full ISO string
  // FullCalendar accepts this format and treats it as a date-only value
  const monthStr = String(localDate.getMonth() + 1).padStart(2, '0');
  const dayStr = String(localDate.getDate()).padStart(2, '0');
  
  return `${localDate.getFullYear()}-${monthStr}-${dayStr}`;
}

/**
 * Parse dates from backend ensuring timezone consistency.
 * This is the main entry point for parsing task dates from the API.
 * 
 * @param dateValue - Date value from backend (string or Date)
 * @returns Properly parsed Date object
 */
export function parseBackendDate(dateValue: string | Date | null | undefined): Date | null {
  return parseTaskDate(dateValue);
}

/**
 * Safe date parsing that validates the result.
 * Returns null for any invalid date.
 * 
 * @param dateValue - Any date value
 * @returns Valid Date object or null
 */
export function safeParseDateOnly(dateValue: any): Date | null {
  if (!dateValue) return null;
  
  const parsed = parseTaskDate(dateValue);
  if (!parsed || isNaN(parsed.getTime())) return null;
  
  return parsed;
}